import { Box, Tooltip } from "@dehub/theme";
import { cloneElement, useId, type MouseEvent, type ReactElement, type ReactNode } from "react";
import classes from "./a11y.module.css";

type Props = Readonly<{
  /** Why the control is unavailable (a permission code, a status). Null or empty: the control is enabled. */
  reason: ReactNode;
  /** One focusable control: Button, ActionIcon, UnstyledButton. Do not pass `disabled`. */
  children: ReactElement<Record<string, unknown>>;
}>;

/**
 * A control that looks and acts disabled but stays reachable: `aria-disabled` + `data-disabled`
 * instead of the native attribute, so keyboard focus lands on it, the tooltip opens on focus as well
 * as hover, and the reason is also wired through `aria-describedby` for screen readers. The click is
 * swallowed here.
 */
export function ReasonTooltip({ reason, children }: Props) {
  const id = useId();
  if (!reason) return children;

  const control = cloneElement(children, {
    "aria-disabled": true,
    "data-disabled": true,
    "aria-describedby": id,
    onClick: (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
    },
  });

  return (
    <>
      <Tooltip label={reason} withArrow events={{ hover: true, focus: true, touch: true }}>
        {control}
      </Tooltip>
      <Box component="span" id={id} className={classes.srOnly}>
        {reason}
      </Box>
    </>
  );
}
